/**
 * Readable client links and the firm's own client-facing copy.
 *
 * Shared by the API (emails, link resolution), the portal (greeting) and the
 * firm app (preview), so all three substitute and validate the same way.
 */

/** Path segments the portal app already owns, so no client may take them. */
export const portalReservedClientSlugs: ReadonlySet<string> = new Set([
  "c",
  "demo",
  "healthz",
  "portal",
  "api",
  "assets",
  "brand",
  "fonts",
  "favicon",
  "robots",
])

export const PORTAL_CLIENT_SLUG_MAX = 60

const CLIENT_SLUG = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/

/** Why a candidate client slug cannot be used, or null when it can. */
export const portalClientSlugProblem = (slug: string): string | null => {
  if (slug.length < 2) return "Use at least 2 characters"
  if (slug.length > PORTAL_CLIENT_SLUG_MAX)
    return `Use ${PORTAL_CLIENT_SLUG_MAX} characters or fewer`
  if (!CLIENT_SLUG.test(slug) || slug.includes("--"))
    return "Use lowercase letters, numbers and single hyphens"
  if (portalReservedClientSlugs.has(slug)) return "That link is reserved"
  return null
}

/** A client name as a link: "Marlow & Finch Salon" → "marlow-finch-salon". */
export const portalClientSlugFrom = (name: string): string => {
  const slug = name
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    // "Tom's" reads as one word in a link: toms, not tom-s.
    .replace(/['’]/g, "")
    .replace(/&/g, " ")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, PORTAL_CLIENT_SLUG_MAX)
    .replace(/-+$/g, "")
  if (slug.length < 2 || portalReservedClientSlugs.has(slug))
    return `client-${slug || "link"}`.slice(0, PORTAL_CLIENT_SLUG_MAX)
  return slug
}

/** The `x-portal-link` value for a readable link. Tokens never contain ':'. */
export const PORTAL_SLUG_LINK_PREFIX = "slug:"

export const portalSlugLinkValue = (slug: string): string =>
  `${PORTAL_SLUG_LINK_PREFIX}${slug}`

/** The client slug in an `x-portal-link` value, or null for a token. */
export const readPortalSlugLink = (value: string): string | null => {
  if (!value.startsWith(PORTAL_SLUG_LINK_PREFIX)) return null
  const slug = value.slice(PORTAL_SLUG_LINK_PREFIX.length)
  return portalClientSlugProblem(slug) === null ? slug : null
}

export const portalCopyPlaceholders = [
  "first_name",
  "client_name",
  "firm_name",
] as const

export type PortalCopyPlaceholder = (typeof portalCopyPlaceholders)[number]

export type PortalCopyValues = Partial<
  Record<PortalCopyPlaceholder, string | null | undefined>
>

/**
 * Fill {first_name}, {client_name} and {firm_name}. A value that is missing
 * drops the placeholder and tidies what it leaves behind, so "Hi {first_name},"
 * reads "Hi," rather than "Hi {first_name},".
 */
export const renderPortalCopy = (
  template: string,
  values: PortalCopyValues
): string =>
  template
    .replace(
      /\{(first_name|client_name|firm_name)\}/g,
      (_, key: string) =>
        portalCopyPlaceholders
          .filter((placeholder) => placeholder === key)
          .map((placeholder) => values[placeholder]?.trim() ?? "")[0] ?? ""
    )
    .replace(/[ \t]+([,.!?])/g, "$1")
    .replace(/[ \t]{2,}/g, " ")
    .trim()

/** The first word of a contact's name, for "Hi {first_name}". */
export const firstNameOf = (name: string | null | undefined): string | null => {
  const first = name?.trim().split(/\s+/)[0]
  return first ? first : null
}

/**
 * Who a client email comes from and where replies go. Every client email
 * comes from the firm: with no From name of its own it carries the firm's
 * name, and with no reply-to, replies go to the firm's contact email, then
 * to the email it gives clients for accounting access. Only a firm with
 * none of them gets replies at LedgerBox's sending address (null here).
 */
export const portalEmailSender = (settings: {
  displayName: string
  emailFromName?: string | null
  emailReplyTo?: string | null
  supportEmail?: string | null
  accountantEmail?: string | null
}): { fromName: string | null; replyTo: string | null } => ({
  fromName:
    settings.emailFromName?.trim() || settings.displayName.trim() || null,
  replyTo:
    settings.emailReplyTo?.trim() ||
    settings.supportEmail?.trim() ||
    settings.accountantEmail?.trim() ||
    null,
})

export const defaultPortalInviteSubject =
  "{firm_name} set up a secure page for your documents"

export const defaultPortalInviteMessage =
  "Hi {first_name}, we use this page to collect your documents. Open your link to see what we need and upload files. No password needed."

/**
 * The sample client every firm-side preview shows: the portal preview and
 * its address bar, and the invite and reminder email previews. The portal
 * demo's in-app profile is this client, so the name in the address bar
 * and the name on the page agree.
 */
export const portalSampleClient = {
  company: "Hartley Plumbing",
  contactName: "Dana Hartley",
  contactEmail: "dana@hartleyplumbing.com",
  slug: "hartley-plumbing",
} as const
